import React from 'react';

import { Chip, Link, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ImageAvatar } from 'components/Avatar';
import { PaperCard } from 'components/Card';

import { isoToDateString } from 'utils/dateFormatter';

import { useAuth } from 'lib/auth';
import { paths } from 'routes';
import { useLocale } from 'i18n/useLocale';

const getFirstSentence = (text) => {
  if (!text) return '';
  const firstSentenceMatch = text.match(/[^.!?]+[.!?]/);
  return firstSentenceMatch ? firstSentenceMatch[0].trim() : text.trim();
};

export const ListItem = ({
  ad_id,
  slug,
  buyer_id,
  buyer_name,
  buyer_slug,
  supplier_id,
  buyer_logo_url,
  supplier_name,
  supplier_slug,
  supplier_logo_url,
  role,
  created_on,
  description,
  owner_type,
  websiteContext = 'suppliers',
  forcePublicUrls = true,
}) => {
  const { t } = useTranslation();
  const { account, user } = useAuth();
  const lang = useLocale();

  const buyer_url = forcePublicUrls
    ? (buyer_slug?.length > 0 ? `${paths.public.companyPublicProfile("buyer", buyer_slug, lang)}` : '')
    : (account?.super_admin || user?.Company?.company_id === buyer_id ) ? paths.buyersDetailsView(buyer_id) : buyer_slug?.length > 0 ? paths.public.companyPublicProfile("buyer", buyer_slug, lang) : "";

  const supplier_url = forcePublicUrls
    ? (supplier_slug?.length > 0 ? `${paths.public.companyPublicProfile("supplier", supplier_slug, lang)}` : '')
    : (account?.super_admin || user?.Company?.company_id === supplier_id ) ? paths.suppliersDetailsView(supplier_id) : supplier_slug?.length > 0 ? paths.public.companyPublicProfile("supplier", supplier_slug, lang) : "";

  const jobAd_url = forcePublicUrls
    ? (slug?.length > 0 ? `${paths.public.jobAdsDetailsView(slug, lang)}` : '')
    : (account?.super_admin || user?.Company?.company_id) ? paths.jobAdsDetailsView(ad_id) : slug?.length > 0 ? paths.public.jobAdsDetailsView(slug, lang) : "";

  return (
    <PaperCard
      sx={{
        flex: 1,
        boxShadow: 0,
        borderRadius: 1,
        p: 2,
        display: "flex",
        flexDirection: 'column',
      }}
    >
      <Link
        component={RouterLink}
        to={jobAd_url}
        color="inherit"
        underline='none'
        sx={{height: '100%', width:1, display: 'flex', flexDirection: 'column'}}
        state={{websiteContext: websiteContext}}
      >
        <Stack flexDirection={"column"} gap={2.5} sx={{flexGrow: 1}}>
          <Stack flexDirection={"row"} gap={{xs:2, lg:3}} alignItems={"center"}>
            <Link
              component={RouterLink}
              to={buyer_url}
              color="inherit"
              underline="none"
              onClick={(e) => e.stopPropagation()}
            >
              <ImageAvatar img={buyer_logo_url || ''} name={buyer_name || ''}
                sx={{
                  width: {xs: 30, md: 40, lg: 48},
                  height: {xs: 30, md: 40, lg: 48},
                }}
              />
            </Link>
            
            <Typography
              variant="h5"
              sx={{
                fontSize: {xs: "0.875rem", md: "1.125rem"},
                fontWeight: {xs: 500, md: 600},
                lineHeight: {xs: 1.4, md: "28px"},
                color: "text.black",
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                minHeight: { xs: '2.45rem', md: '56px' },
              }}
            >
              {role}
            </Typography>
          </Stack>
          {owner_type && (
            <Chip
              label={t(`website:jobAdCard.ownerTypes.${owner_type}`, { defaultValue: owner_type })}
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
                lineHeight: "20px",
                color: "primary.light",
                backgroundColor: "#4B6BFB0D",
                borderRadius: "6px",
                width: "fit-content",
              }}
            />
          )}
          <Stack justifyContent="space-between" gap={2} sx={{flexGrow: 1}}>
            <Stack gap={2} flexGrow={1}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: {xs: "0.75rem", md: "1rem"},
                    fontWeight: 400,
                    lineHeight: 1.5,
                    color: "text.list",
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 3,
                    minHeight: { xs: '3.375rem', md: '4.5rem' },
                  }}
                >
                  {getFirstSentence(description)}
              </Typography>
            </Stack>
            <Stack flexDirection={"row"} sx={{alignItems: "center", justifyContent: "space-between"}}>
              <Stack>
                <Typography variant="body3" fontSize={'0.875rem'} fontWeight={400} lineHeight={"24px"}>
                  {isoToDateString(created_on, false)}
                </Typography>
              </Stack>
              <Stack flexDirection={"row"} gap={1} sx={{alignItems: "center"}}>
                {supplier_slug?.length > 0 ? (
                  <Link
                    component={RouterLink}
                    to={supplier_url}
                    color="inherit"
                    underline="none"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ImageAvatar img={supplier_logo_url || ''} name={supplier_name || ''} fontSize='1rem'
                      sx={{
                        width: {xs: 20, md: 24},
                        height: {xs: 20, md: 24},
                        fontSize: {xs: "0.875rem", md: "1rem"}
                      }}
                    />
                  </Link>
                ) : (
                  <ImageAvatar img={supplier_logo_url || ''} name={supplier_name || ''} fontSize='1rem'
                    sx={{
                      width: {xs: 20, md: 24},
                      height: {xs: 20, md: 24},
                      fontSize: {xs: "0.875rem", md: "1rem"}
                    }}
                  />
                )}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Link>
    </PaperCard>
  );
};
